import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Flex, VStack, Text, Input, Button, Spinner, Image,
  Card, CardBody, CardHeader, Heading, SimpleGrid, FormControl, FormLabel,
  Icon, type IconProps,
} from '@chakra-ui/react';
import { Badge, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import {
  reprocessTrack,
  getStreamUrl,
  fetchFilterOptions,
  activateTrack,
  deactivateTrack,
  updateLibraryTrack,
  getAffectedPlaylists,
  batchRemoveTracksFromPlaylist,
} from '../../api/libraryApi';
import type { LibraryTrackDto } from '../../api/libraryApi';
import { ManagePlaylistsModal } from './components/ManagePlaylistsModal';
import { useAuth } from '../auth/AuthProvider';
import { TrackStatusBadge } from './components/TrackStatusBadge';
import { LicenseSelect } from './components/LicenseSelect';
import { useHlsPlayer } from '../../hooks/useHlsPlayer';
import { ReprocessConfirmDialog } from './components/ReprocessConfirmDialog';
import { HardDeleteDialog } from './HardDeleteDialog';
import { TagInput } from './components/TagInput';
import { AutocompleteInput } from './components/AutocompleteInput';

type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

/** Today's date as "YYYY-MM-DD" (local). */
function todayISODate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Derived "Rights -" indicator (016-track-licensing) — NOT a stored field.
 * Warns on active tracks that lack a valid license.
 */
function isRightsMinus(track: {
  status: 'active' | 'inactive';
  licenseId: string | null;
  rightsValidTo: string | null;
}): boolean {
  return (
    track.status === 'active' &&
    (!track.licenseId ||
      (track.rightsValidTo != null && track.rightsValidTo < todayISODate()))
  );
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Placeholder icon for missing cover art */
function MusicNoteIcon(props: IconProps) {
  return (
    <Icon viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6Z"
      />
    </Icon>
  );
}

export function TrackDetailPage() {
  const { t } = useTranslation();
  const { trackId } = useParams<{ trackId: string }>();
  const queryClient = useQueryClient();

  const toast = useToast();
  const [showManagePlaylists, setShowManagePlaylists] = useState(false);

  const { data: track, isLoading } = useQuery({
    queryKey: ['admin-track', trackId],
    queryFn: () => apiClient.get<LibraryTrackDto>(`/admin/library/${trackId}`),
    enabled: Boolean(trackId),
  });

  // Which curated playlists this track is in (the "In playlists" card).
  const { data: trackPlaylists } = useQuery({
    queryKey: ['admin-track-playlists', trackId],
    queryFn: () => getAffectedPlaylists(trackId!),
    enabled: Boolean(trackId),
  });

  const removeFromPlaylist = useMutation({
    mutationFn: (playlistId: string) => batchRemoveTracksFromPlaylist(playlistId, [trackId!]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-track-playlists', trackId] });
      queryClient.invalidateQueries({ queryKey: ['admin-playlist'] });
      toast({ title: t('admin.playlists.manage.done'), status: 'success', duration: 2500, isClosable: true });
    },
    onError: () => toast({ title: t('admin.playlists.manage.failed'), status: 'error', duration: 4000, isClosable: true }),
  });

  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [album, setAlbum] = useState('');
  const [genre, setGenre] = useState('');
  const [mood, setMood] = useState('');
  const [tempo, setTempo] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  // ── Licensing / rights state ──
  const [licenseId, setLicenseId] = useState<string | null>(null);
  const [licenseName, setLicenseName] = useState<string | null>(null);
  const [rightsHolder, setRightsHolder] = useState('');
  const [rightsTerritory, setRightsTerritory] = useState('');
  const [contract, setContract] = useState('');
  const [rightsHolderTrackId, setRightsHolderTrackId] = useState('');
  const [rightsValidFrom, setRightsValidFrom] = useState('');
  const [rightsValidTo, setRightsValidTo] = useState('');
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [sasToken, setSasToken] = useState<string | null>(null);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [showHardDeleteDialog, setShowHardDeleteDialog] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const { admin } = useAuth();
  const isSystemAdmin = admin?.role === 'system_admin';

  const { data: filterOptions } = useQuery({
    queryKey: ['admin-library-filter-options'],
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    if (track) {
      setTitle(track.title);
      setArtist(track.artist);
      setAlbum(track.album ?? '');
      setGenre(track.genre ?? '');
      setMood(track.mood ?? '');
      setTempo(track.tempo ?? '');
      setTags(track.tags ?? []);
      setLicenseId(track.licenseId ?? null);
      setLicenseName(track.licenseName ?? null);
      setRightsHolder(track.rightsHolder ?? '');
      setRightsTerritory(track.rightsTerritory ?? '');
      setContract(track.contract ?? '');
      setRightsHolderTrackId(track.rightsHolderTrackId ?? '');
      setRightsValidFrom(track.rightsValidFrom ?? '');
      setRightsValidTo(track.rightsValidTo ?? '');
    }
  }, [track]);

  React.useEffect(() => {
    if (track?.processingStatus === 'completed' && trackId) {
      getStreamUrl(trackId).then(r => {
        setStreamUrl(r.streamUrl);
        setSasToken(r.sasToken);
      }).catch(() => {
        setStreamUrl(null);
        setSasToken(null);
      });
    } else {
      setStreamUrl(null);
      setSasToken(null);
    }
  }, [track?.processingStatus, trackId]);

  const update = useMutation({
    mutationFn: () => {
      if (!title.trim()) {
        setTitleError(t('validation.nameRequired', { field: t('admin.library.trackTitle') }));
        throw new Error('Validation failed');
      }
      // The track-detail form is AUTHORITATIVE for licensing/rights fields — send
      // the full current state. null clears license/dates; "" is fine for text.
      return updateLibraryTrack(trackId!, {
        title: title || null,
        artist: artist || null,
        album: album || null,
        genre: genre || null,
        mood: mood || null,
        tempo: tempo || null,
        tags,
        licenseId,
        rightsHolder: rightsHolder || null,
        rightsTerritory: rightsTerritory || null,
        contract: contract || null,
        rightsHolderTrackId: rightsHolderTrackId || null,
        rightsValidFrom: rightsValidFrom || null,
        rightsValidTo: rightsValidTo || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-track', trackId] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-filter-options'] });
    },
  });

  const toggleActive = useMutation({
    mutationFn: () =>
      track?.status === 'active' ? deactivateTrack(trackId!) : activateTrack(trackId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-track', trackId] }),
  });

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => reprocessTrack(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-track', trackId] });
      queryClient.invalidateQueries({ queryKey: ['admin-library'] });
      setShowReprocessDialog(false);
    },
  });

  const isCompleted = track?.processingStatus === 'completed';

  const player = useHlsPlayer({
    streamUrl: isCompleted ? streamUrl : null,
    sasToken: isCompleted ? sasToken : null,
  });

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  if (!track) return <Text>{t('admin.common.noResults')}</Text>;

  const isProcessing = track.processingStatus === 'pending' || track.processingStatus === 'processing';
  const canReprocess = isCompleted || track.processingStatus === 'failed';
  const isActive = track.status === 'active';
  const rightsMinus = isRightsMinus(track);

  return (
    <VStack spacing={5} align="stretch" pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="track-detail-page">
      {/* ── Header Card: Cover Art with Player + Identity ── */}
      <Card data-testid="track-header-card">
        <CardBody>
          <Flex gap={6} align="flex-start" direction={{ base: 'column', md: 'row' }}>
            {/* Cover Art with play/pause overlay */}
            <Box flexShrink={0}>
              <Box
                w={140}
                h={140}
                borderRadius="xl"
                overflow="hidden"
                position="relative"
                role="group"
                cursor={isCompleted ? 'pointer' : undefined}
                onClick={isCompleted ? player.toggle : undefined}
                data-testid={isCompleted ? 'track-play-button' : undefined}
              >
                {track.thumbnailUrl ? (
                  <Image
                    src={track.thumbnailUrl}
                    alt={track.title}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    data-testid="track-cover-image"
                  />
                ) : (
                  <Flex
                    w="100%"
                    h="100%"
                    bg="gray.100"
                    align="center"
                    justify="center"
                    data-testid="track-cover-placeholder"
                  >
                    <MusicNoteIcon boxSize={12} color="gray.400" />
                  </Flex>
                )}
                {isCompleted && (
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    bg="blackAlpha.500"
                    opacity={player.isPlaying ? 1 : 0}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.15s"
                    borderRadius="xl"
                  >
                    {player.isLoading ? (
                      <Spinner size="lg" color="white" />
                    ) : (
                      <Icon viewBox="0 0 24 24" w={10} h={10} color="white">
                        {player.isPlaying
                          ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor" />
                          : <path d="M8 5v14l11-7L8 5z" fill="currentColor" />}
                      </Icon>
                    )}
                  </Flex>
                )}
              </Box>
              {/* Hidden video element for HLS.js */}
              <video
                ref={player.videoRef as React.RefObject<HTMLVideoElement>}
                style={{ display: 'none' }}
                preload="metadata"
                data-testid="track-player-section"
              />
            </Box>

            {/* Artist / Meta */}
            <VStack align="flex-start" spacing={2} flex={1} minW={0}>
              <Text fontSize="md" color="gray.600" data-testid="track-artist-display">
                {track.artist}
              </Text>
              <Flex gap={3} align="center" flexWrap="wrap">
                <TrackStatusBadge status={track.processingStatus as ProcessingStatus} />
                <Badge
                  colorScheme={isActive ? 'green' : 'gray'}
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="10px"
                  textTransform="uppercase"
                  data-testid="track-lifecycle-badge"
                >
                  {isActive ? t('admin.library.statusActive') : t('admin.library.statusInactive')}
                </Badge>
                {rightsMinus && (
                  <Badge
                    colorScheme="orange"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="10px"
                    textTransform="uppercase"
                    data-testid="rights-minus-badge"
                  >
                    {t('admin.library.rightsMinus')}
                  </Badge>
                )}
                <Text fontSize="sm" color="gray.500">
                  {formatDuration(track.durationSeconds)}
                </Text>
              </Flex>
              {track.processingError && (
                <Text color="red.500" fontSize="sm" data-testid="processing-error">
                  {track.processingError}
                </Text>
              )}

              {/* Inline progress bar (visible when playing or after seeking) */}
              {isCompleted && (player.isPlaying || player.currentTime > 0) && (
                <Flex align="center" gap={2} w="100%" data-testid="track-player-progress">
                  <Box
                    flex={1}
                    h="6px"
                    bg="gray.200"
                    borderRadius="sm"
                    overflow="hidden"
                    cursor="pointer"
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.stopPropagation();
                      if (!player.duration) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percent = x / rect.width;
                      player.seek(percent * player.duration);
                    }}
                  >
                    <Box
                      h="100%"
                      w={`${player.duration > 0 ? (player.currentTime / player.duration) * 100 : 0}%`}
                      bg="blue.400"
                      borderRadius="sm"
                    />
                  </Box>
                  <Text fontSize="xs" color="gray.500" minW="70px" textAlign="right">
                    {formatTime(player.currentTime)} / {formatTime(player.duration)}
                  </Text>
                </Flex>
              )}
              {player.error && (
                <Text fontSize="xs" color="red.500">{t(player.error)}</Text>
              )}
            </VStack>
          </Flex>
        </CardBody>
      </Card>

      {/* ── Track Information Card (editable) ── */}
      <Card data-testid="track-info-card">
        <CardHeader pb={0}>
          <Heading size="sm">{t('admin.library.trackDetail.trackInfo')}</Heading>
          <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.trackInfoDesc')}</Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isInvalid={!!titleError}>
                <FormLabel>{t('admin.library.trackTitle')}</FormLabel>
                <Input value={title} onChange={(e) => { setTitle(e.target.value); setTitleError(null); }} data-testid="input-title" />
                {titleError && <Text color="red.500" fontSize="sm" mt={1}>{titleError}</Text>}
              </FormControl>
              <FormControl>
                <FormLabel>{t('admin.library.artist')}</FormLabel>
                <Input value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="input-artist" />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>{t('admin.library.album')}</FormLabel>
              <Input value={album} onChange={(e) => setAlbum(e.target.value)} data-testid="input-album" />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel>{t('admin.library.style')}</FormLabel>
                <AutocompleteInput
                  value={genre}
                  onChange={setGenre}
                  suggestions={filterOptions?.genres ?? []}
                  data-testid="input-genre"
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('admin.library.mood')}</FormLabel>
                <AutocompleteInput
                  value={mood}
                  onChange={setMood}
                  suggestions={filterOptions?.moods ?? []}
                  data-testid="input-mood"
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('admin.library.tempo')}</FormLabel>
                <AutocompleteInput
                  value={tempo}
                  onChange={setTempo}
                  suggestions={filterOptions?.tempos ?? []}
                  data-testid="input-tempo"
                />
              </FormControl>
            </SimpleGrid>

            <Box data-testid="tag-input-section">
              <TagInput
                tags={tags}
                onChange={setTags}
                suggestions={filterOptions?.tags ?? []}
              />
            </Box>

            {/* ── Licensing & Rights ── */}
            <Box data-testid="licensing-section">
              <Heading size="xs" mb={3} color="gray.600">{t('admin.library.licensingHeading')}</Heading>
              <VStack spacing={4} align="stretch">
                <LicenseSelect
                  value={licenseName}
                  onChange={(id, name) => {
                    setLicenseId(id);
                    setLicenseName(name);
                  }}
                />

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>{t('admin.library.rightsHolder')}</FormLabel>
                    <Input value={rightsHolder} onChange={(e) => setRightsHolder(e.target.value)} data-testid="input-rights-holder" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('admin.library.rightsTerritory')}</FormLabel>
                    <Input value={rightsTerritory} onChange={(e) => setRightsTerritory(e.target.value)} data-testid="input-rights-territory" />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>{t('admin.library.contract')}</FormLabel>
                    <Input value={contract} onChange={(e) => setContract(e.target.value)} data-testid="input-contract" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('admin.library.rightsHolderTrackId')}</FormLabel>
                    <Input value={rightsHolderTrackId} onChange={(e) => setRightsHolderTrackId(e.target.value)} data-testid="input-rights-holder-track-id" />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>{t('admin.library.rightsValidFrom')}</FormLabel>
                    <Input type="date" value={rightsValidFrom} onChange={(e) => setRightsValidFrom(e.target.value)} data-testid="input-rights-valid-from" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('admin.library.rightsValidTo')}</FormLabel>
                    <Input type="date" value={rightsValidTo} onChange={(e) => setRightsValidTo(e.target.value)} data-testid="input-rights-valid-to" />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </Box>

            <Flex justify="flex-end">
              <Button
                colorScheme="blue"
                onClick={() => update.mutate()}
                isDisabled={update.isPending}
                data-testid="btn-save"
              >
                {update.isPending ? <Spinner size="sm" /> : t('common.save')}
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>

      {/* ── Track Details Card (read-only) ── */}
      <Card data-testid="track-details-card">
        <CardHeader pb={0}>
          <Heading size="sm">{t('admin.library.trackDetail.details')}</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box>
              <Text fontSize="sm" color="gray.500">{t('admin.library.duration')}</Text>
              <Text fontWeight="medium">{formatDuration(track.durationSeconds)}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">{t('admin.library.columns.status')}</Text>
              <TrackStatusBadge status={track.processingStatus as ProcessingStatus} />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.createdAt')}</Text>
              <Text fontWeight="medium">{formatDate(track.createdAt)}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.updatedAt')}</Text>
              <Text fontWeight="medium">{formatDate(track.updatedAt)}</Text>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* ── In Playlists Card ── */}
      <Card data-testid="track-playlists-card">
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Heading size="sm">{t('admin.library.trackDetail.inPlaylists')}</Heading>
            <Button size="sm" colorScheme="brand" onClick={() => setShowManagePlaylists(true)} data-testid="track-add-to-playlists">
              {t('admin.playlists.manage.action')}
            </Button>
          </Flex>
        </CardHeader>
        <CardBody>
          {!trackPlaylists || trackPlaylists.affectedPlaylists.length === 0 ? (
            <Text fontSize="sm" color="gray.500" data-testid="track-playlists-empty">
              {t('admin.library.trackDetail.notInPlaylists')}
            </Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {trackPlaylists.affectedPlaylists.map((p) => (
                <Flex key={p.playlistId} justify="space-between" align="center" data-testid={`track-playlist-${p.playlistId}`}>
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{p.playlistName}</Text>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="red"
                    isDisabled={removeFromPlaylist.isPending}
                    onClick={() => removeFromPlaylist.mutate(p.playlistId)}
                    data-testid={`track-playlist-remove-${p.playlistId}`}
                  >
                    {t('common.remove', 'Remove')}
                  </Button>
                </Flex>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* ── Actions Card ── */}
      <Card data-testid="track-actions-card">
        <CardHeader pb={0}>
          <Heading size="sm">{t('admin.library.trackDetail.actions')}</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={3} align="stretch">
            {/* Reprocess */}
            {canReprocess && (
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium">{t('admin.library.reprocess.button')}</Text>
                  <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.reprocessDesc')}</Text>
                </Box>
                <Button
                  colorScheme="orange"
                  size="sm"
                  onClick={() => setShowReprocessDialog(true)}
                  isDisabled={reprocessMutation.isPending}
                  data-testid="reprocess-button"
                >
                  {reprocessMutation.isPending ? <Spinner size="sm" /> : t('admin.library.reprocess.button')}
                </Button>
              </Flex>
            )}
            {isProcessing && (
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium">{t('admin.library.reprocess.button')}</Text>
                  <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.reprocessDesc')}</Text>
                </Box>
                <Button colorScheme="orange" size="sm" isDisabled opacity={0.5} data-testid="reprocess-button-disabled">
                  {t('admin.library.reprocess.button')}
                </Button>
              </Flex>
            )}

            {/* Active / Inactive toggle */}
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="medium">
                  {isActive ? t('admin.library.deactivate') : t('admin.library.activate')}
                </Text>
                <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.visibilityDesc')}</Text>
              </Box>
              <Button
                colorScheme={isActive ? 'red' : 'green'}
                size="sm"
                variant="outline"
                onClick={() => toggleActive.mutate()}
                isDisabled={toggleActive.isPending}
                data-testid="btn-toggle-active"
              >
                {isActive ? t('admin.library.deactivate') : t('admin.library.activate')}
              </Button>
            </Flex>

            {/* Hard delete — system admins only */}
            {isSystemAdmin && (
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium" color="red.500">{t('admin.library.hardDelete.button')}</Text>
                  <Text fontSize="sm" color="gray.500">{t('admin.library.trackDetail.deleteDesc')}</Text>
                </Box>
                <Button
                  colorScheme="red"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowHardDeleteDialog(true)}
                  data-testid="hard-delete-button"
                >
                  {t('admin.library.hardDelete.button')}
                </Button>
              </Flex>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Dialogs */}
      {showReprocessDialog && trackId && (
        <ReprocessConfirmDialog
          trackId={trackId}
          open={showReprocessDialog}
          onClose={() => setShowReprocessDialog(false)}
          onConfirm={() => reprocessMutation.mutate(trackId)}
          isConfirming={reprocessMutation.isPending}
        />
      )}
      {showHardDeleteDialog && trackId && track && (
        <HardDeleteDialog
          trackId={trackId}
          trackTitle={track.title}
          isOpen={showHardDeleteDialog}
          onClose={() => setShowHardDeleteDialog(false)}
        />
      )}

      {showManagePlaylists && trackId && (
        <ManagePlaylistsModal
          open={showManagePlaylists}
          trackIds={[trackId]}
          onClose={() => setShowManagePlaylists(false)}
          onDone={() => queryClient.invalidateQueries({ queryKey: ['admin-track-playlists', trackId] })}
        />
      )}
    </VStack>
  );
}
